import "./themes.scss";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { startActionWithPromise } from "../../helpers/saga-promise-helpers";
import useMergeState from "../../hooks/mergeState";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Spinner,
  CardFooter,
} from "reactstrap";

import config from "../../config/config";

import { searchUserReportsAction } from "../../redux/reducers/userReportsReducer";

import { getProductsAction } from "../../redux/reducers/subscriptionReducer";

import DataTable from "../../components/DataTable";
import PageSizeSelector from "../../components/base/PageSizeSelector";
import Paging from "../../components/base/Paging";

import FilterForm from "./filterForm";
import { getGridColumns } from "./gridColumns";
import ReportDetailsModal from "./ReportDetailsModal";

const UserReports = () => {
  const dispatch = useDispatch();

  const [detailsModal, setDetailsModal] = useState(null);

  const [filters, setFilters] = useMergeState({
    pageSize: config.DEFAULT_PAGESIZE,
    pageNumber: 1,
    sort: "PLAN",
    order: "desc",
    reload: false,
  });

  const [basePlans, setBasePlans] = useState([]);
  const [addonPlans, setAddonPlans] = useState([]);

  const fetchData = (params) => {
    (async () => {
      try {
        await startActionWithPromise(
          searchUserReportsAction,
          {
            params,
            successCallback: () => {},
            failedCallback: () => {},
          },
          dispatch
        );
      } catch {}
    })();
  };

  useEffect(() => {
    if (filters.reload) {
      const params = {
        pageSize: filters.pageSize,
        pageNumber: filters.pageNumber - 1,
      };

      if (filters?.userId) params.userId = filters.userId;

      if (filters?.selectedPlans && filters?.selectedPlans.length) {
        params.plans = filters.selectedPlans;
      }

      if (filters?.selectedAddons && filters?.selectedAddons.length) {
        params.addons = filters.selectedAddons;
      }

      if (filters?.selectedOverdues && filters?.selectedOverdues.length) {
        params.paymentFlags = filters.selectedOverdues;
      }

      if (filters?.selectedStorages && filters?.selectedStorages.length) {
        params.storageFlags = filters.selectedStorages;
      }

      if (filters?.selectedRevenues && filters?.selectedRevenues.length) {
        params.paidoutFlags = filters.selectedRevenues;
      }

      if (filters.sort && filters.order) {
        params.sort = {
          fieldName: filters.sort,
          dir: filters.order.toUpperCase(),
        };
      }

      fetchData(params);
      setFilters({ ...filters, reload: false });
    }
  }, [filters.reload]);

  useEffect(() => {
    // load plans
    (async () => {
      const resp = await startActionWithPromise(
        getProductsAction,
        { successCallback: () => {}, failedCallback: () => {} },
        dispatch
      );
      if (resp?.length) {
        let _basePlans = [];
        let _addonPlans = [];
        resp.map((prod) => {
          const isBase = prod.type === "BASE";
          prod.plans.map((plan) => {
            if (plan.active && plan.listed) {
              if (isBase) _basePlans.push({name: plan.planGroup, planName:plan.planName.replace('Monthly','').trim()});
              else _addonPlans.push({name: plan.planGroup, planName:plan.planName.replace('Monthly','').trim()});
            }
          });
        });
        setBasePlans(_basePlans);
        setAddonPlans(_addonPlans);
      }
    })();

    const params = {
      pageSize: filters.pageSize,
      pageNumber: filters.pageNumber - 1,
    };
    if (filters.sort && filters.order) {
      params.sort = {
        fieldName: filters.sort,
        dir: filters.order.toUpperCase(),
      };
    }
    fetchData(params);
  }, []);

  const handleColumnSort = (field, order) => {
    if (filters.sort !== field || filters.order !== order)
      setFilters({
        ...filters,
        sort: field,
        order: order,
        reload: true,
      });
  };

  const onSizeChange = (size) => {
    setFilters({ ...filters, pageSize: size, pageNumber: 1, reload: true });
  };

  const onPageChange = (p) => {
    setFilters({ ...filters, pageNumber: p, reload: true });
  };

  const toggleDetailModal = (userId, detailType, title) => {
    if (!userId) setDetailsModal(null);
    else
      setDetailsModal({ userId: userId, detailType: detailType, title: title });
  };

  const gridColumns = getGridColumns(handleColumnSort, toggleDetailModal);

  const userReports =
    useSelector((state) => state.userReportsStates?.userReports) || [];
  const fetching =
    useSelector((state) => state.userReportsStates?.fetching) || false;
  const totalPages =
    useSelector((state) => state.userReportsStates?.totalPages) || 0;
  const pageNumber =
    useSelector((state) => state.userReportsStates?.pageNumber) || 0;
  const totalCount =
    useSelector((state) => state.userReportsStates?.total) || 0;

  return (
    <>
      <h1 className="page-title">Users Summary Report</h1>
      <Card className="w-100 h-100 overflow-auto">
        <CardHeader>
          <FilterForm
            mergeFilters={setFilters}
            loading={fetching}
            userId={filters.userId}
            plans={basePlans}
            addons={addonPlans}
          />
          <Row className="mt-1">
            <Col lg="3" className="mt-1 mb-1 align-self-center"></Col>
            <Col lg="9" className="d-flex">
              <div className="ml-auto">
                <span>Total found:</span>
                <span className="pl-1 totalLine">{totalCount}</span>
              </div>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="pt-0 h-100 data-list">
          {fetching && (
            <div className="text-center w-100 bg-white p-4 position-static h-100">
              <Spinner />
            </div>
          )}
          {!fetching && (
            <DataTable
              keyField="id"
              className="h-100"
              data={userReports}
              columns={gridColumns}
              sort={filters.sort}
              order={filters.order}
              hideSelectColumn={true}
            />
          )}
          {!fetching && (
            <Row className="mt-1">
              <Col lg="3" md="4" className="d-flex">
                <PageSizeSelector
                  size={filters.pageSize}
                  onChange={onSizeChange}
                />
              </Col>
              <Col lg="9" md="8" className="d-flex justify-content-end">
                <Paging
                  totalPages={totalPages}
                  current={pageNumber}
                  show={5}
                  onSelect={onPageChange}
                />
              </Col>
            </Row>
          )}
        </CardBody>
        <CardFooter>
          <Container fluid className="p-0">
            <Row>
              <Col lg="12"></Col>
            </Row>
          </Container>
        </CardFooter>
      </Card>
      {detailsModal && (
        <ReportDetailsModal
          userId={detailsModal?.userId}
          detailType={detailsModal?.detailType}
          title={detailsModal?.title}
          toggleDetailModal={toggleDetailModal}
        />
      )}
    </>
  );
};

export default UserReports;
